import React, { useState, useMemo } from "react";

import { CollapsibleSection } from "../CollapsibleSection";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import { formatCurrency } from "../viewerHelpers";
import Field from "./Field";
import StatusBadge from "./StatusBadge";
import ExpandCollapseToggle from "./ExpandCollapseToggle";
import Raw277ClaimRow from "./Raw277ClaimRow";
import {
  parseRaw277Data,
  getCategoryColor,
  getCategoryDescription,
} from "./raw277Helpers";

const Raw276or277Viewer: React.FC<{
  data: unknown[];
  transactionType: "276" | "277";
}> = ({ data, transactionType }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [allExpanded, setAllExpanded] = useState(true);

  const claims = useMemo(() => parseRaw277Data(data), [data]);

  const selectedClaim = selectedIndex !== null ? claims[selectedIndex] : null;

  const title =
    transactionType === "276"
      ? "276 Claim Status Request"
      : "277 Claim Status Response";
  const isRequest = transactionType === "276";

  if (claims.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No claim status information found in this {transactionType} file.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center flex-wrap gap-2">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <StatusBadge
            status={isRequest ? "REQUEST" : "CLAIM_STATUS"}
            type="type"
          />
          <span className="text-sm text-gray-500">
            {claims.length} claim(s)
          </span>
          <div className="flex-1" />
          {selectedClaim && (
            <button
              onClick={() => setSelectedIndex(null)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Back to list
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 scrollbar-thin">
        {!selectedClaim ? (
          // Claims list view
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-sm text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Patient Ctrl #</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-left">Service Date</th>
                    <th className="px-3 py-2 text-right">Charge</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim, idx) => (
                    <Raw277ClaimRow
                      key={idx}
                      claim={claim}
                      index={idx}
                      isSelected={idx === selectedIndex}
                      onSelect={() => setSelectedIndex(idx)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Claim detail view
          <div>
            <div className="mb-4 flex items-center gap-2">
              <ExpandCollapseToggle
                expanded={allExpanded}
                onToggle={setAllExpanded}
              />
            </div>

            <CollapsibleSection
              title="Claim Reference"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Patient Control #"
                  value={selectedClaim.patientControlNumber}
                />
                <Field
                  label="Payer Claim Control #"
                  value={selectedClaim.payerClaimControlNumber}
                />
                <Field label="Trace Number" value={selectedClaim.traceNumber} />
                <Field
                  label="Bill Type Code"
                  value={selectedClaim.billTypeCode}
                />
                <Field
                  label="Service Date From"
                  value={formatDisplayDate(selectedClaim.serviceDateFrom)}
                />
                <Field
                  label="Service Date To"
                  value={formatDisplayDate(selectedClaim.serviceDateTo)}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Status Information"
              defaultOpen={allExpanded}
            >
              {selectedClaim.statusInfos.map((status, idx) => (
                <div key={idx} className="p-3 bg-white rounded border mb-2">
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`px-2 py-0.5 rounded text-md font-medium ${getCategoryColor(status.categoryCode)}`}
                    >
                      {status.categoryCode}
                    </span>
                    <span className="text-sm font-medium">
                      {getCategoryDescription(status.categoryCode)}
                    </span>
                    {status.date && (
                      <span className="text-sm text-gray-500 ml-auto">
                        {formatDisplayDate(status.date)}
                      </span>
                    )}
                  </div>
                  <div
                    className="grid gap-4 text-sm"
                    style={{
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(120px, 1fr))",
                    }}
                  >
                    <div>
                      <span className="text-sm text-gray-500">Status Code</span>
                      <div className="font-mono">
                        {status.statusCode || "-"}
                      </div>
                    </div>
                    {status.entityCode && (
                      <div>
                        <span className="text-sm text-gray-500">Entity</span>
                        <div>{status.entityCode}</div>
                      </div>
                    )}
                    {status.chargeAmount !== undefined && (
                      <div>
                        <span className="text-sm text-gray-500">Charge</span>
                        <div>{formatCurrency(status.chargeAmount)}</div>
                      </div>
                    )}
                    {status.paidAmount !== undefined && (
                      <div>
                        <span className="text-sm text-gray-500">Paid</span>
                        <div className="text-green-600">
                          {formatCurrency(status.paidAmount)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </CollapsibleSection>

            <CollapsibleSection
              title="Patient / Subscriber"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Subscriber"
                  value={selectedClaim.subscriberName}
                />
                <Field
                  label="Subscriber ID"
                  value={selectedClaim.subscriberId}
                />
                {selectedClaim.dependentName && (
                  <Field
                    label="Dependent (Patient)"
                    value={selectedClaim.dependentName}
                  />
                )}
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Payer / Provider"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="Payer" value={selectedClaim.payerName} />
                <Field label="Payer ID" value={selectedClaim.payerId} />
                <Field label="Provider" value={selectedClaim.providerName} />
                <Field label="Provider NPI" value={selectedClaim.providerNpi} />
                <Field label="Receiver" value={selectedClaim.receiverName} />
              </div>
            </CollapsibleSection>

            {selectedClaim.serviceLines.length > 0 && (
              <CollapsibleSection
                title={`Service Lines (${selectedClaim.serviceLines.length})`}
                defaultOpen={allExpanded}
              >
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-sm text-gray-500 uppercase">
                        <th className="px-3 py-2 text-left">Line</th>
                        <th className="px-3 py-2 text-left">Code</th>
                        <th className="px-3 py-2 text-left">Service Date</th>
                        <th className="px-3 py-2 text-right">Units</th>
                        <th className="px-3 py-2 text-right">Charge</th>
                        <th className="px-3 py-2 text-right">Paid</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedClaim.serviceLines.map((line, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="px-3 py-2 text-md">{idx + 1}</td>
                          <td className="px-3 py-2 text-md font-mono">
                            {line.procedureCode || "-"}
                          </td>
                          <td className="px-3 py-2 text-md text-gray-600">
                            {formatDisplayDate(line.serviceDate)}
                          </td>
                          <td className="px-3 py-2 text-md text-right">
                            {line.units ?? "-"}
                          </td>
                          <td className="px-3 py-2 text-md text-right">
                            {formatCurrency(line.chargeAmount)}
                          </td>
                          <td className="px-3 py-2 text-md text-right text-green-600">
                            {formatCurrency(line.paidAmount)}
                          </td>
                          <td className="px-3 py-2 text-md">
                            {line.statusInfos.map((si, i) => (
                              <span
                                key={i}
                                className={`inline-block px-1.5 py-0.5 rounded text-sm mr-1 ${getCategoryColor(si.categoryCode)}`}
                              >
                                {si.categoryCode}
                              </span>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Raw276or277Viewer;
